import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
import UserNewCreateForm from 'src/pages/dashboard/accessrole/user-new-create-form';
import AdminUserViewDetails from 'src/pages/dashboard/accessrole/view/admin-user-view-user';
import AppUserProfessionalView from 'src/sections/user/view/app-professional-view';
import AppUserFacilityView from 'src/sections/user/view/app-facility-view';
import AdminUserEditDetails from 'src/sections/user/view/admin-user-edit-user';


// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));

// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));


// TOUR

// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));




const AdvertisementPage = lazy(() => import('src/pages/dashboard/finance/advertisementdetails'));
const ViewSubcriptionPage = lazy(() => import('src/pages/dashboard/finance/subcription'));
const ViewAdvertisementPage = lazy(() => import('src/pages/dashboard/finance/viewAdvertisement'));
const AddProductPage = lazy(() => import('src/pages/dashboard/finance/addProduct'));
const ViewProduct = lazy(() => import('src/pages/dashboard/finance/viewProduct'));



const AddAdvertisementPage = lazy(() => import('src/pages/dashboard/finance/advertisement'));
const EditAdvertisementPage = lazy(() => import('src/pages/dashboard/finance/editAdvertisement'));


const NewInsuPage = lazy(() => import('src/pages/dashboard/verification/newinsu'));
const CertificatePage = lazy(() => import('src/pages/dashboard/verification/newcertificate'));
const ModeratedPostPage = lazy(() => import('src/pages/dashboard/verification/moderated'));

const RolesPage = lazy(() => import('src/pages/dashboard/accessrole/Roles'));
const ViewRolesPage = lazy(() => import('src/pages/dashboard/accessrole/viewrole'));
const AdminUserPage = lazy(() => import('src/pages/dashboard/accessrole/adminUser'));
const DeletePermissionPage = lazy(() => import('src/pages/dashboard/accessrole/deletePermission'));
const AddRolePage = lazy(() => import('src/pages/dashboard/accessrole/addRoles'));
const EditRolePage = lazy(() => import('src/pages/dashboard/accessrole/editRoles'));

const ProductDetailsPage = lazy(() => import('src/pages/dashboard/finance/details'));

const NewSparkfeedPage = lazy(() => import('src/pages/dashboard/user/newSparkfeed'));
const AddSparkfeed = lazy(() => import('src/pages/dashboard/user/addSparkfeed'));
const ViewCategoryPage = lazy(() => import('src/pages/dashboard/user/viewcategory'));
const AppUserViewDetails = lazy(() => import('src/sections/user/view/app-user-view-user'));

const AppUSerPage = lazy(() => import('src/pages/dashboard/user/appUser'));
const AppFacilityViewDetails = lazy(() => import('src/sections/user/view/app-user-view-facility'));
const AppUserEditDetails = lazy(() => import('src/sections/user/view/app-user-edit-user'));


const AppFacilityEditDetails = lazy(() => import('src/sections/user/view/app-user-edit-facility'));
const AppProfessionalEditDetails = lazy(
  () => import('src/sections/user/view/app-user-edit-professional')
);
const AppProfessionalViewDetails = lazy(
  () => import('src/sections/user/view/app-user-view-professional')
);

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },

      {
        path: 'verification',
        children: [
          { element: <NewInsuPage />, index: true },
          { path: 'insurance', element: <NewInsuPage /> },
          { path: 'certificate', element: <CertificatePage /> },
          { path: 'moderated', element: <ModeratedPostPage /> },
        ],
      },

      {
        path: 'user',
        children: [
          { element: <UserProfilePage />, index: true },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'list', element: <UserListPage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'account', element: <UserAccountPage /> },
          { path: 'appUser', element: <AppUSerPage /> },
          { path: 'adminUser', element: <AdminUserPage /> },
          { path: ':id/newSparkfeed', element: <NewSparkfeedPage /> },
          { path: 'addSparkfeed', element: <AddSparkfeed /> },
          { path: ':id/viewCategory', element: <ViewCategoryPage /> },
          { path: 'userdetails/:id', element: <AppUserViewDetails /> },
          { path: 'admindetails/edit/:id', element: <AdminUserEditDetails /> },
          
          {
            path: 'appUser',
            children: [
              { element: <AppUSerPage />, index: true },
              { path: 'userdetails/:id', element: <AppUserViewDetails /> },
              { path: 'userdetails/edit/:id', element: <AppUserEditDetails /> },
            ],
          },
          {
            path: 'facility',
            children: [
              { element: <AppUserFacilityView />, index: true },
              { path: 'userdetails/:id', element: <AppFacilityViewDetails /> },
              { path: 'userdetails/edit/:id', element: <AppFacilityEditDetails /> },
            ],
          },

          {
            path: 'professional',
            children: [
              { element: <AppUserProfessionalView />, index: true },
              { path: 'userdetails/:id', element: <AppProfessionalViewDetails /> },
              { path: 'userdetails/edit/:id', element: <AppProfessionalEditDetails /> },
            ],
          },
        
        ],
      },
     

      {
        path: 'finance',
        children: [
          { element: <AdvertisementPage />, index: true },
          { path: 'advertisementdetails', element: <AdvertisementPage /> },
          { path: 'addadvertisement', element: <AddAdvertisementPage /> },
          { path: ':id/viewAdvertisement', element: <ViewAdvertisementPage /> },
          { path: ':id/editAdvertisement', element: <EditAdvertisementPage /> },
          { path: 'editProduct/:id', element: <AddProductPage /> },
          { path: 'addProduct', element: <AddProductPage /> },
          { path: 'viewProduct', element: <ViewProduct /> },
          { path: 'details/:id', element: <ProductDetailsPage /> },
          { path: 'subscription', element: <ViewSubcriptionPage /> },
        ],
      },

      {
        path: 'accessrole',
        children: [
          { element: <RolesPage />, index: true },
          { path: 'permissions', element: <RolesPage /> },
          { path: 'adminUser', element: <AdminUserPage /> },
          { path: 'deletePermission', element: <DeletePermissionPage /> },
          { path: ':id/editRoles', element: <EditRolePage /> },
          { path: 'addRoles', element: <AddRolePage /> },
          { path: ':id/viewRoles', element: <ViewRolesPage /> },
          { path: 'new', element: <UserNewCreateForm /> },
          { path: 'admindetails/:id', element: <AdminUserViewDetails /> },

        ],
      },
      // {
      //   path: 'order',
      //   children: [
      //     { element: <OrderListPage />, index: true },
      //     { path: 'list', element: <OrderListPage /> },
      //     { path: ':id', element: <OrderDetailsPage /> },
      //   ],
      // },
      // {
      //   path: 'invoice',
      //   children: [
      //     { element: <InvoiceListPage />, index: true },
      //     { path: 'list', element: <InvoiceListPage /> },
      //     { path: ':id', element: <InvoiceDetailsPage /> },
      //     { path: ':id/edit', element: <InvoiceEditPage /> },
      //     { path: 'new', element: <InvoiceCreatePage /> },
      //   ],
      // },
      // {
      //   path: 'post',
      //   children: [
      //     { element: <BlogPostsPage />, index: true },
      //     { path: 'list', element: <BlogPostsPage /> },
      //     { path: ':title', element: <BlogPostPage /> },
      //     { path: ':title/edit', element: <BlogEditPostPage /> },
      //     { path: 'new', element: <BlogNewPostPage /> },
      //   ],
      // },
      // {
      //   path: 'job',
      //   children: [
      //     { element: <JobListPage />, index: true },
      //     { path: 'list', element: <JobListPage /> },
      //     { path: ':id', element: <JobDetailsPage /> },
      //     { path: 'new', element: <JobCreatePage /> },
      //     { path: ':id/edit', element: <JobEditPage /> },
      //   ],
      // },
      // {
      //   path: 'tour',
      //   children: [
      //     { element: <TourListPage />, index: true },
      //     { path: 'list', element: <TourListPage /> },
      //     { path: ':id', element: <TourDetailsPage /> },
      //     { path: 'new', element: <TourCreatePage /> },
      //     { path: ':id/edit', element: <TourEditPage /> },
      //   ],
      // },
      // { path: 'file-manager', element: <FileManagerPage /> },
      // { path: 'mail', element: <MailPage /> },
      // { path: 'chat', element: <ChatPage /> },
      // { path: 'calendar', element: <CalendarPage /> },
      // { path: 'kanban', element: <KanbanPage /> },
      // { path: 'permission', element: <PermissionDeniedPage /> },
      // { path: 'blank', element: <BlankPage /> },
    ],
  },
];
